import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, FileText, Phone, Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import { NotesDialog } from './NotesDialog';
import { format, addDays, startOfDay, endOfDay, isSameDay } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useLanguage } from '@/contexts/LanguageContext';
import { CustomerAvailabilityService } from '@/lib/services/customerAvailabilityService';

// Utility function to convert Western numerals to Arabic-Indic numerals
const toArabicNumerals = (str: string) => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return str.replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};
import type { Appointment } from '@/types';

export function AppointmentList() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [sendingInvitation, setSendingInvitation] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { currentUser } = useAuth();
  const { t, dir, language } = useLanguage();
  const dateLocale = language === 'ar' ? ar : enUS;

  useEffect(() => {
    if (currentUser?.uid) {
      fetchAppointments();
    }
  }, [currentUser]);

  // Set up real-time listener for appointments changes
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Get start of today to listen only to future appointments
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfToday = Timestamp.fromDate(today);

    // Listen for appointments changes for this coach
    const appointmentsQuery = query(
      collection(db, 'appointments'),
      where('coachId', '==', currentUser.uid),
      where('date', '>=', startOfToday)
    );

    const unsubscribe = onSnapshot(appointmentsQuery, (snapshot) => {
      // Check if there are any changes (added, modified, or removed)
      const hasChanges = snapshot.docChanges().length > 0;
      
      if (hasChanges) {
        console.log('[AppointmentList] Appointments changed, refreshing...', {
          changes: snapshot.docChanges().map(change => ({
            type: change.type,
            id: change.doc.id
          }))
        });
        
        // Refresh the appointments list
        fetchAppointments();
      }
    }, (error) => {
      console.error('[AppointmentList] Error in appointments listener:', error);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Set up real-time listener for user data changes (optimized)
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Only listen for changes to users that are actually customers
    // This is more efficient than listening to all users
    const usersQuery = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      // Only check for changes in customer data that affects current appointments
      const hasRelevantChanges = snapshot.docChanges().some(change => {
        const data = change.doc.data();
        const roles = data.roles;
        const isCustomer = Array.isArray(roles) && roles.some(r => r.role === 'customer');
        
        // Only trigger refresh if it's a customer and the change affects display data
        return isCustomer && (
          change.type === 'modified' && (
            change.doc.data().firstNameEn !== change.doc.metadata.fromCache ||
            change.doc.data().lastNameEn !== change.doc.metadata.fromCache ||
            change.doc.data().phone !== change.doc.metadata.fromCache
          )
        );
      });
      
      if (hasRelevantChanges) {
        // Debounce the refresh to avoid excessive calls
        setTimeout(() => fetchAppointments(), 500);
      }
    });

    return () => unsubscribe();
  }, [currentUser?.uid]); // Only depend on uid, not the entire currentUser object

  const fetchAppointments = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      console.log('[AppointmentList] Current user UID:', currentUser.uid);
      console.log('[AppointmentList] Current timestamp:', Timestamp.now().toDate());
      
      // Get start of today to include all appointments from today onwards
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startOfToday = Timestamp.fromDate(today);
      
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('coachId', '==', currentUser.uid),
        where('date', '>=', startOfToday)
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      console.log('[AppointmentList] Future appointments found:', appointmentsSnapshot.docs.length);
      
      // Also check for any appointments (including past) for debugging
      const allAppointmentsQuery = query(
        collection(db, 'appointments'),
        where('coachId', '==', currentUser.uid)
      );
      const allAppointmentsSnapshot = await getDocs(allAppointmentsQuery);
      console.log('[AppointmentList] Total appointments for this coach:', allAppointmentsSnapshot.docs.length);
      
      // Get all unique customer IDs
      const customerIds = new Set(
        appointmentsSnapshot.docs.map(doc => doc.data().customerId)
      );
      console.log('[AppointmentList] customerIds in appointments:', Array.from(customerIds));

      // Fetch all users and filter for customers in code
      const customersSnapshot = await getDocs(collection(db, 'users'));
      const customerDocs = customersSnapshot.docs.filter(doc => {
        const roles = doc.data().roles;
        return Array.isArray(roles) && roles.some(r => r.role === 'customer');
      });
      // Create a map of customer IDs to names and phones
      const customerInfo = new Map();
      customerDocs.forEach(doc => {
        const data = doc.data();
        customerInfo.set(doc.id, {
          nameEn: `${data.firstNameEn || data.firstName || ''} ${data.lastNameEn || data.lastName || ''}`.trim(),
          nameAr: `${data.firstNameAr || ''} ${data.lastNameAr || ''}`.trim(),
          phone: data.phone
        });
      });
      console.log('[AppointmentList] customerInfo map:', Array.from(customerInfo.entries()));

      // Find unmatched customerIds
      const unmatched = Array.from(customerIds).filter(id => !customerInfo.has(id));
      if (unmatched.length > 0) {
        console.warn('[AppointmentList] Unmatched customerIds (no user found):', unmatched);
      }

      const appointmentsData = appointmentsSnapshot.docs.map(doc => {
        const data = doc.data();
        const customerData = customerInfo.get(data.customerId);
        return {
          id: doc.id,
          ...data,
          date: data.date.toDate(), // Convert Timestamp to Date
          customerNameEn: customerData?.nameEn || 'Unknown Customer',
          customerNameAr: customerData?.nameAr || 'مستفيد غير معروف',
          customerPhone: customerData?.phone
        };
      });

      setAppointments(appointmentsData.sort((a, b) => a.date.getTime() - b.date.getTime()));
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (appointmentId: string, status: 'completed' | 'cancelled') => {
    try {
      setProcessingId(appointmentId);
      await updateDoc(doc(db, 'appointments', appointmentId), {
        status,
        updatedAt: Timestamp.now()
      });
      
      setAppointments(prev => prev.map(apt => 
        apt.id === appointmentId ? { ...apt, status } : apt
      ));
      
      toast.success(t(`dashboard.coach.appointments.status.${status}`));
    } catch (error) {
      console.error('Error updating appointment status:', error);
      toast.error(t('dashboard.coach.appointments.errors.updateFailed'));
    } finally {
      setProcessingId(null);
    }
  };

  const handleSendInvitation = async (appointment: Appointment) => {
    if (!currentUser || !appointment.customerPhone) return;

    try {
      setSendingInvitation(appointment.id);

      // Get customer name
      const customerName = language === 'ar' 
        ? appointment.customerNameAr || appointment.customerNameEn || 'مستفيد'
        : appointment.customerNameEn || 'Customer';

      // Get coach name from current user data
      const coachName = language === 'ar'
        ? `${currentUser.displayName || 'مدرب'}`
        : `${currentUser.displayName || 'Coach'}`;

      // Send availability link with appointment info
      const result = await CustomerAvailabilityService.sendAvailabilityLinkToCustomer(
        currentUser.uid,
        coachName,
        appointment.customerId,
        customerName,
        appointment.customerPhone,
        language,
        {
          date: appointment.date instanceof Date ? appointment.date : appointment.date.toDate(),
          status: appointment.status
        }
      );

      if (result.success) {
        toast.success(
          language === 'ar' 
            ? 'تم إرسال الدعوة بنجاح'
            : 'Invitation sent successfully'
        );
      } else {
        toast.error(
          language === 'ar' 
            ? `فشل في إرسال الدعوة: ${result.error}`
            : `Failed to send invitation: ${result.error}`
        );
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error(
        language === 'ar' 
          ? 'فشل في إرسال الدعوة'
          : 'Failed to send invitation'
      );
    } finally {
      setSendingInvitation(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 md:p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="text-center py-6 md:py-8 text-muted-foreground">
        {t('dashboard.coach.appointments.noAppointments')}
      </div>
    );
  }

  // Filter appointments for the selected date
  const filteredAppointments = appointments.filter(appointment => 
    isSameDay(appointment.date, selectedDate)
  );

  // Navigation handlers
  const handlePreviousDay = () => {
    setSelectedDate(prev => addDays(prev, -1));
  };

  const handleNextDay = () => {
    setSelectedDate(prev => addDays(prev, 1));
  };

  // Check if selected date is today
  const isToday = isSameDay(selectedDate, new Date());

  return (
    <div className="space-y-3 md:space-y-4" dir={dir}>
      {/* Day Navigation */}
      <div className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded-lg">
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePreviousDay}
          className="h-8 w-8 p-0"
        >
          {dir === 'rtl' ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
        
        <div className="flex flex-col items-center">
          <span className="text-sm font-medium">
            {isToday 
              ? (language === 'ar' ? 'اليوم' : 'Today')
              : format(selectedDate, 'PPP', { locale: dateLocale })
            }
          </span>
          {filteredAppointments.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {filteredAppointments.length} {language === 'ar' ? 'موعد' : 'appointment'}
              {filteredAppointments.length !== 1 && (language === 'ar' ? '' : 's')}
            </span>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNextDay}
          className="h-8 w-8 p-0"
        >
          {dir === 'rtl' ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </div>

      {/* Appointments for selected date */}
      {filteredAppointments.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          {language === 'ar' ? 'لا توجد مواعيد في هذا اليوم' : 'No appointments on this day'}
        </div>
      ) : (
        filteredAppointments.map((appointment) => (
        <div
          key={appointment.id}
          className="flex flex-col gap-3 p-3 md:p-4 border rounded-lg bg-card"
        >
          {/* Customer Info */}
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
              <span className="font-medium text-sm md:text-base">
                {language === 'ar' 
                  ? (appointment.customerNameAr || appointment.customerNameEn || 'مستفيد غير معروف')
                  : (appointment.customerNameEn || 'Unknown Customer')
                }
              </span>
              {appointment.customerPhone && (
                <a 
                  href={`tel:${appointment.customerPhone}`}
                  className="inline-flex items-center text-xs md:text-sm text-muted-foreground hover:text-primary min-h-[32px]"
                >
                  <Phone className={`h-3 w-3 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                  <span className="truncate">{appointment.customerPhone}</span>
                </a>
              )}
              <Badge 
                variant={
                  appointment.status === 'completed' ? 'success' :
                  appointment.status === 'cancelled' ? 'destructive' :
                  'secondary'
                }
                className="text-xs"
              >
                {t(`dashboard.coach.appointments.status.${appointment.status}`)}
              </Badge>
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">
              {language === 'ar' 
                ? toArabicNumerals(format(appointment.date instanceof Date ? appointment.date : appointment.date.toDate(), 'PPP p', { locale: dateLocale }))
                : format(appointment.date instanceof Date ? appointment.date : appointment.date.toDate(), 'PPP p', { locale: dateLocale })
              }
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1.5 md:gap-2">
            {appointment.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-green-500 hover:bg-green-500 hover:text-white w-full sm:flex-1 text-xs md:text-sm h-8 md:h-9"
                  onClick={() => handleUpdateStatus(appointment.id, 'completed')}
                  disabled={!!processingId}
                >
                  {processingId === appointment.id ? (
                    <Loader2 className="h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                      {t('dashboard.coach.appointments.actions.attended')}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-500 hover:bg-red-500 hover:text-white w-full sm:flex-1 text-xs md:text-sm h-8 md:h-9"
                  onClick={() => handleUpdateStatus(appointment.id, 'cancelled')}
                  disabled={!!processingId}
                >
                  {processingId === appointment.id ? (
                    <Loader2 className="h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                  ) : (
                    <>
                      <XCircle className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                      {t('dashboard.coach.appointments.actions.notAttended')}
                    </>
                  )}
                </Button>
              </>
            )}
            <Button
              size="sm"
              onClick={() => setSelectedAppointment(appointment)}
              className="bg-[#004250] text-white hover:bg-[#00303a] w-full sm:flex-1 text-xs md:text-sm h-8 md:h-9"
            >
              <FileText className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
              {language === 'ar' ? 'فتح الملف' : 'Open File'}
            </Button>
            {appointment.customerPhone && (
              <Button
                size="sm"
                onClick={() => handleSendInvitation(appointment)}
                disabled={sendingInvitation === appointment.id}
                className="bg-white hover:bg-gray-100 text-gray-900 border border-gray-300 w-full sm:flex-1 text-xs md:text-sm h-8 md:h-9"
              >
                {sendingInvitation === appointment.id ? (
                  <Loader2 className={`h-3.5 w-3.5 md:h-4 md:w-4 animate-spin ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                ) : (
                  <>
                    <Bell className={`h-3.5 w-3.5 md:h-4 md:w-4 ${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'}`} />
                    {language === 'ar' ? 'إرسال دعوة' : 'Send Invitation'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      ))
      )}
      {selectedAppointment && (
        <NotesDialog
          appointment={selectedAppointment}
          open={!!selectedAppointment}
          onOpenChange={() => setSelectedAppointment(null)}
          onNotesUpdate={(appointmentId, notes) => {
            // Update the appointment in state with new notes
            setAppointments(prev => prev.map(apt => 
              apt.id === appointmentId ? { ...apt, notes } : apt
            ));
            // Also update selectedAppointment so next time it opens with correct notes
            setSelectedAppointment(prev => 
              prev && prev.id === appointmentId ? { ...prev, notes } : prev
            );
          }}
        />
      )}
    </div>
  );
}